import { Box, Stack } from '@mui/material'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { SelectOneAppHook } from 'components/core/search/apps'
import { RelativeTimestamp } from 'components/core/time/display'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppUserAccountType } from 'components/pages/app/users/account_type'
import { AppUserDisplayName } from 'components/pages/app/users/app-user-display-name'
import { C1EntitlementDisplayNameWithFetch } from 'components/pages/entitlement/display-name'
import { QUERIES } from 'components/pages/explore/query/Query'
import { StructuredSearchStyles } from 'components/pages/explore/query/styles'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { AppValue } from 'lib/actions/apps'
import { AppUserValueWithEntitlementValue } from 'lib/actions/apps/app-entitlement'
import { useInitialFilters } from 'lib/search'
import { useRouter } from 'next/router'
import { SearchAppUsersWithStandingHighRiskGrantsRequest } from 'pbts/c1/api/app/v1/app_entitlement_user_binding'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { searchAppUsersWithStandingHighRiskGrants } from 'redux_toolkit/entitlements'
import { ExportData } from './ExportData'

type StandingPrivilegesTableProps = {
    appId: string | undefined,
}

const cols: Array<C1TableReduxColumn<AppUserValueWithEntitlementValue>> = [
    {
        field: 'accountOwner',
        label: <FormattedMessage id="standing_privilege_report_account_owner" defaultMessage="Account owner" />,
        Component: ({ row }) => <Box>
            {row.user ?
                <UserDisplayName user={row.user} showTooltip showUserStatus /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    {
        field: 'account',
        label: <FormattedMessage id="standing_privilege_report_account" defaultMessage="Account" />,
        Component: ({ row }) => <AppUserDisplayName appUser={row.appUser} showTooltip />,
        maxWidth: '275px',
    },
    {
        field: 'app',
        label: <FormattedMessage id="standing_privilege_report_app" defaultMessage="App" />,
        Component: ({ row }) => <Box>
            {row.app ?
                <AppDisplayName app={row.app} avatarSize="xs" /> :
                <C1EmptyIndicator />
            }
        </Box>,
    },
    {
        field: 'entitlement',
        label: <FormattedMessage id="standing_privilege_report_entitlement" defaultMessage="Entitlement" />,
        Component: ({ row }): React.ReactElement => {
            return useMemo(() => (
                row.entitlement && row.app
                    ? <Box sx={{ overflow: 'hidden' }}>
                        <C1EntitlementDisplayNameWithFetch entitlementId={row.entitlement.id} appId={row.entitlement.appId} />
                    </Box>
                    : <C1EmptyIndicator />
            ), [row.app, row.entitlement])
        },
        maxWidth: '300px',
    },
    {
        field: 'accountType',
        label: <FormattedMessage id="standing_privilege_report_account_type" defaultMessage="Account type" />,
        Component: ({ row }) => <AppUserAccountType appUser={row.appUser} />,
    },
    {
        field: 'lastLogin',
        label: <FormattedMessage id="standing_privilege_report_last_login" defaultMessage="Last login" />,
        Component: ({ row }) => <Box>
            {row.appUser?.lastUsedAt && row.appUser.lastUsedAt.seconds ?
                <RelativeTimestamp variant="date_only" date={row.appUser.lastUsedAt} /> :
                <C1EmptyIndicator />
            }
        </Box>
        ,
    },
]

export const StandingPrivilegesTable: FC<StandingPrivilegesTableProps> = ({ appId }) => {
    const [page, setPage] = useState<number>(0)
    const { data, isError, isLoading, getPage, canLoadMore, error } =
        searchAppUsersWithStandingHighRiskGrants.useSearchAppUsersWithStandingHighRiskGrants(
            SearchAppUsersWithStandingHighRiskGrantsRequest.create({
                appId,
                expandMask: { paths: ['*'] },
            }),
            {},
        )

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.StandingPrivileges)

    const appUsersAndEntitlementsTable = useMemo(
        () => (
            <C1TableRedux
                data={data}
                cols={cols}
                canLoadMore={canLoadMore}
                isError={isError}
                error={error}
                isLoading={isLoading}
                page={page}
                onPageChange={onPageChange}
            />
        ),
        [data, isLoading, isError, error, canLoadMore, page, onPageChange],
    )

    return <List<AppUserValueWithEntitlementValue> table={appUsersAndEntitlementsTable} />
}

export const StandingPrivilegesReport: FC = () => {
    const router = useRouter()
    const initialFilters = useInitialFilters(SearchAppUsersWithStandingHighRiskGrantsRequest)
    const [selectedAppId, setSelectedAppId] = useState<string | undefined>(initialFilters.appId)

    const onSelectApp = useCallback((app: AppValue | null) => {
        setSelectedAppId(app?.id)
        router.query.appId = app?.id
        router.replace({ pathname: router.pathname, query: router.query }, undefined, { shallow: true })
    }, [router])

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.STANDING_PRIVILEGES,
            request: {
                oneofKind: 'searchAppUsersWithStandingHighRiskGrantsRequest',
                searchAppUsersWithStandingHighRiskGrantsRequest: SearchAppUsersWithStandingHighRiskGrantsRequest.create({
                    appId: selectedAppId,
                    expandMask: { paths: ['*'] },
                }),
            },
        })
    }, [selectedAppId])

    return <>
        <Stack spacing={2} direction="row" justifyContent="space-between" alignItems="flex-end" pr={1}>
            <SelectOneAppHook
                onSelected={onSelectApp}
                initAppId={initialFilters.appId}
                sx={sxc(StructuredSearchStyles.selectApp, StructuredSearchStyles.filterInputs)}
            />
            <ExportData exportDataRequest={exportDataActionRequest} />
        </Stack>
        <StandingPrivilegesTable appId={selectedAppId} />
    </>
}
